import React, { useState, useEffect, useRef } from 'react';
import { Upload, Link as LinkIcon, X } from 'lucide-react';

/**
 * Image Editor
 * Replace images by uploading files or providing URLs
 */
export default function ImageEditor({ element, onUpdate }) {
  const [imageUrl, setImageUrl] = useState('');
  const [altText, setAltText] = useState('');
  const [uploadMethod, setUploadMethod] = useState('url'); // 'url' or 'file'
  const [width, setWidth] = useState('w-12');
  const [height, setHeight] = useState('h-12');
  const [objectFit, setObjectFit] = useState('object-contain');
  const [isVisible, setIsVisible] = useState(true);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (element?.src) {
      setImageUrl(element.src);
    }
    if (element?.alt) {
      setAltText(element.alt);
    }
    
    // Extract current dimensions from element's classes
    if (element?.element) {
      const classList = Array.from(element.element.classList);
      const widthClass = classList.find(c => c.startsWith('w-'));
      const heightClass = classList.find(c => c.startsWith('h-'));
      const objectFitClass = classList.find(c => c.startsWith('object-'));
      const isHidden = classList.includes('hidden');
      
      if (widthClass) setWidth(widthClass);
      if (heightClass) setHeight(heightClass);
      if (objectFitClass) setObjectFit(objectFitClass);
      setIsVisible(!isHidden);
    }
  }, [element]);

  const handleVisibilityChange = (newVisibility) => {
    setIsVisible(newVisibility);
    
    // 1. Try to update a 'visible' property in the data if likely exists
    if (element?.dataPath) {
        // Heuristic: replace .src with .visible
        // e.g. hero.image.src -> hero.image.visible
        if (element.dataPath.endsWith('.src')) {
            const visiblePath = element.dataPath.replace(/\.src$/, '.visible');
            onUpdate(visiblePath, newVisibility);
        } else {
             // If dataPath points to the image object directly?
             // Usually VisualEditor passes path to the bound attribute (src)
             onUpdate(`${element.dataPath}.visible`, newVisibility);
        }
    }
    
    // 2. Also update the class to reflect immediately in preview (optional, but good for UX)
    /* 
       Note: The data update should trigger a re-render of the preview. 
       If the template uses the 'visible' property, it will hide.
    */
  };

  const handleUrlChange = (e) => {
    setImageUrl(e.target.value);
  };

  const handleUrlSubmit = () => {
    if (!imageUrl || !element?.dataPath) return;
    
    // Update image URL in data
    onUpdate(element.dataPath, imageUrl);
  };

  const handleAltTextChange = (e) => {
    setAltText(e.target.value);
  };

  const handleAltTextSubmit = () => {
    if (!element?.dataPath) return;
    
    // Update alt text
    const altPath = element.dataPath.replace(/\.src$/, '.alt');
    onUpdate(altPath, altText);
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size should be less than 5MB');
      return;
    }

    try {
      // Convert to base64 for preview
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64Url = event.target.result;
        setImageUrl(base64Url);
        
        // Update image in data
        if (element?.dataPath) {
          onUpdate(element.dataPath, base64Url);
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image');
    }
  };

  const handleChooseFile = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveImage = () => {
    setImageUrl('');
    if (element?.dataPath) {
      onUpdate(element.dataPath, '');
    }
  };

  const handleDimensionChange = (property, value) => {
    const stylePath = element?.element?.getAttribute('data-style-path');
    
    if (!stylePath) {
      console.warn('⚠️ No data-style-path found for image, cannot update dimensions');
      return;
    }
    
    console.log(`🖼️ Updating image ${property}: ${stylePath}.${property} → ${value}`);
    onUpdate(`${stylePath}.${property}`, value);
    
    // Update local state
    if (property === 'width') setWidth(value);
    if (property === 'height') setHeight(value);
    if (property === 'objectFit') setObjectFit(value);
  };

  return (
    <div className="space-y-4">
      {/* Current Image Preview */}
      {imageUrl && (
        <div className="relative">
          <img
            src={imageUrl}
            alt={altText || 'Preview'}
            className="w-full h-32 object-cover rounded-lg border border-gray-300"
          />
          <button
            onClick={handleRemoveImage}
            className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
            title="Remove image"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Visibility & Removal */}
      <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
        <label className="block text-xs font-medium text-gray-700 mb-2">
          Visibility & Actions
        </label>
        
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm text-gray-600">Visible</span>
          <button
            onClick={() => handleVisibilityChange(!isVisible)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              isVisible ? 'bg-blue-600' : 'bg-gray-200'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                isVisible ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
        
        <button
          onClick={handleRemoveImage}
          className="w-full px-3 py-2 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-md border border-red-200 transition-colors flex items-center justify-center gap-2"
        >
          <X className="w-3 h-3" />
          Remove Image Source
        </button>
        <p className="text-[10px] text-gray-400 mt-2 text-center">
            "Visible" requires your template to support a 'visible' boolean property. "Remove" clears the URL.
        </p>
      </div>

      {/* Upload Method Selector */}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-2">
          Replace Image
        </label>
        <div className="flex gap-2 mb-3">
          <button
            onClick={() => setUploadMethod('url')}
            className={`flex-1 px-3 py-2 text-xs rounded-lg transition-colors flex items-center justify-center gap-2 ${
              uploadMethod === 'url'
                ? 'bg-blue-500 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            <LinkIcon className="w-4 h-4" />
            URL
          </button>
          <button
            onClick={() => setUploadMethod('file')}
            className={`flex-1 px-3 py-2 text-xs rounded-lg transition-colors flex items-center justify-center gap-2 ${
              uploadMethod === 'file'
                ? 'bg-blue-500 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            <Upload className="w-4 h-4" />
            Upload
          </button>
        </div>
      </div>

      {/* URL Input */}
      {uploadMethod === 'url' && (
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-2">
            Image URL
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={imageUrl}
              onChange={handleUrlChange}
              onBlur={handleUrlSubmit}
              placeholder="https://example.com/image.jpg"
              className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={handleUrlSubmit}
              className="px-4 py-2 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              Apply
            </button>
          </div>
        </div>
      )}

      {/* File Upload */}
      {uploadMethod === 'file' && (
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
          />
          <button
            onClick={handleChooseFile}
            className="w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors flex flex-col items-center gap-2"
          >
            <Upload className="w-6 h-6 text-gray-400" />
            <span className="text-sm text-gray-600">Choose image file</span>
            <span className="text-xs text-gray-400">Max 5MB • JPG, PNG, GIF, WebP</span>
          </button>
        </div>
      )}

      {/* Alt Text */}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-2">
          Alt Text (Accessibility)
        </label>
        <input
          type="text"
          value={altText}
          onChange={handleAltTextChange}
          onBlur={handleAltTextSubmit}
          placeholder="Describe the image..."
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <p className="text-xs text-gray-500 mt-1">
          Helps screen readers and SEO
        </p>
      </div>

      {/* Dimensions & Object Fit */}
      <div className="border-t border-gray-200 pt-4">
        <label className="block text-xs font-medium text-gray-700 mb-3">
          Dimensions
        </label>
        
        {/* Width */}
        <div className="mb-3">
          <label className="block text-xs text-gray-600 mb-1">Width</label>
          <select
            value={width}
            onChange={(e) => handleDimensionChange('width', e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="w-8">w-8 (2rem / 32px)</option>
            <option value="w-10">w-10 (2.5rem / 40px)</option>
            <option value="w-12">w-12 (3rem / 48px)</option>
            <option value="w-16">w-16 (4rem / 64px)</option>
            <option value="w-20">w-20 (5rem / 80px)</option>
            <option value="w-24">w-24 (6rem / 96px)</option>
            <option value="w-32">w-32 (8rem / 128px)</option>
            <option value="w-40">w-40 (10rem / 160px)</option>
            <option value="w-48">w-48 (12rem / 192px)</option>
            <option value="w-64">w-64 (16rem / 256px)</option>
            <option value="w-full">w-full (100%)</option>
            <option value="w-auto">w-auto (auto)</option>
          </select>
        </div>

        {/* Height */}
        <div className="mb-3">
          <label className="block text-xs text-gray-600 mb-1">Height</label>
          <select
            value={height}
            onChange={(e) => handleDimensionChange('height', e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="h-8">h-8 (2rem / 32px)</option>
            <option value="h-10">h-10 (2.5rem / 40px)</option>
            <option value="h-12">h-12 (3rem / 48px)</option>
            <option value="h-16">h-16 (4rem / 64px)</option>
            <option value="h-20">h-20 (5rem / 80px)</option>
            <option value="h-24">h-24 (6rem / 96px)</option>
            <option value="h-32">h-32 (8rem / 128px)</option>
            <option value="h-40">h-40 (10rem / 160px)</option>
            <option value="h-48">h-48 (12rem / 192px)</option>
            <option value="h-64">h-64 (16rem / 256px)</option>
            <option value="h-full">h-full (100%)</option>
            <option value="h-auto">h-auto (auto)</option>
          </select>
        </div>

        {/* Object Fit */}
        <div>
          <label className="block text-xs text-gray-600 mb-1">Object Fit</label>
          <select
            value={objectFit}
            onChange={(e) => handleDimensionChange('objectFit', e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="object-contain">Contain (fit inside)</option>
            <option value="object-cover">Cover (fill area)</option>
            <option value="object-fill">Fill (stretch)</option>
            <option value="object-none">None (original size)</option>
            <option value="object-scale-down">Scale Down</option>
          </select>
          <p className="text-xs text-gray-500 mt-1">
            How the image fits within its container
          </p>
        </div>
      </div>
    </div>
  );
}

